<template>
    <div class="task-create">
        
      <div class="create-container">
        <!-- 左侧表单区域 -->
        <div class="form-section">
          <h2 class="section-title">创建挂宝任务</h2>
          
          <!-- 步骤指示器 -->
          <div class="steps-indicator">
            <div 
              v-for="(step, index) in steps" 
              :key="step.key"
              class="step-item"
              :class="{
                'active': currentStep === index,
                'complete': currentStep > index
              }"
            >
              <div class="step-number">{{ index + 1 }}</div>
              <span class="step-text">{{ step.title }}</span>
            </div>
          </div>
  
          <!-- 步骤1: 账号信息填写 -->
          <div v-if="currentStep === 0" class="form-container">
            <div class="form-group">
              <label>Twitch账号</label>
              <input 
                type="text" 
                v-model="formData.account"
                placeholder="输入Twitch账号"
                class="form-input"
              >
            </div>
  
            <div class="form-group">
              <label>账号密码</label>
              <div class="password-input">
                <input 
                  :type="showPassword ? 'text' : 'password'"
                  v-model="formData.password"
                  placeholder="输入账号密码"
                  class="form-input"
                >
                <button 
                  @click="togglePassword" 
                  class="toggle-password"
                >
                  <span>👁️</span>
                </button>
              </div>
            </div>
  
            <div class="form-group">
              <label>卡密</label>
              <input 
                type="text" 
                v-model="formData.cdkey"
                placeholder="输入卡密"
                class="form-input"
              >
            </div>
  
            <div class="form-actions">
              <button 
                @click="nextStep" 
                class="submit-btn"
                :disabled="!isStep1Valid"
              >
                下一步
              </button>
              <button @click="goToTaskList" class="cancel-btn">
                返回任务列表
              </button>
            </div>
          </div>
  
          <!-- 步骤2: 选择游戏 -->
          <div v-if="currentStep === 1" class="form-container">
            <div class="form-group">
              <label>选择游戏</label>
              <select 
                v-model="formData.gameId" 
                class="form-select"
                @change="handleGameChange"
              >
                <option value="">请选择目标游戏</option>
                <option 
                  v-for="game in games" 
                  :key="game.id" 
                  :value="game.id"
                >
                  {{ game.name }}
                </option>
              </select>
            </div>

            <!-- 修改主播添加区域的提示文案 -->
            <div class="form-group">
              <label>
                添加关注主播
                <span class="optional-tag">可选</span>
              </label>
              <div class="streamer-input-wrapper">
                <input 
                  type="text" 
                  v-model="streamerInput"
                  placeholder="可以添加1-3个想要关注的主播"
                  class="form-input"
                  @keyup.enter="addStreamer"
                  :disabled="formData.subs.length >= 3"
                >
                <span class="streamer-count">{{ formData.subs.length }}/3</span>
              </div>
              
              <!-- 已添加主播列表 -->
              <div class="streamers-list" v-if="formData.subs.length > 0">
                <div 
                  v-for="streamer in formData.subs" 
                  :key="streamer"
                  class="streamer-tag"
                  @click="removeStreamer(streamer)"
                >
                  <span class="streamer-name">{{ streamer }}</span>
                  <span class="remove-icon">×</span>
                </div>
              </div>
              
              <p class="input-hint" v-if="formData.subs.length < 3">
                可以继续添加 {{ 3 - formData.subs.length }} 个主播
              </p>
              <p class="input-hint" v-else>
                已达到最大添加数量
              </p>
            </div>

            <div class="form-actions">
              <button 
                @click="submitInitialData" 
                class="submit-btn"
                :disabled="isSubmitting || !isStep2Valid"
              >
                {{ isSubmitting ? '提交中...' : '下一步' }}
              </button>
              <button @click="previousStep" class="cancel-btn">
                上一步
              </button>
            </div>
          </div>
  
          <!-- 步骤3: 设备授权 -->
          <div v-if="currentStep === 2" class="form-container">
            <div class="auth-section">
              <div class="auth-code">
                <h3>您的授权码</h3>
                <div class="code-display">
                  {{ authorizationCode }}
                  <button 
                    @click="copyAuthCode" 
                    class="copy-btn"
                    :class="{ 'copied': isCopied }"
                  >
                    {{ isCopied ? '已复制' : '复制' }}
                  </button>
                </div>
              </div>
              
              <div class="auth-instructions">
                <h4>授权步骤：</h4>
                <ol>
                  <li>复制上方授权码</li>
                  <li>打开 <a :href="authUrl" target="_blank">授权页面</a></li>
                  <li>粘贴授权码并完成授权</li>
                  <li>授权完成后返回此页面，点击下一步</li>
                </ol>
              </div>
            </div>
  
            <div class="form-actions">
              <button 
                @click="verifyAuthorization" 
                class="submit-btn"
                :disabled="isVerifying"
              >
                {{ isVerifying ? '验证中...' : '下一步' }}
              </button>
              <button @click="previousStep" class="cancel-btn">
                上一步
              </button>
            </div>
          </div>
  
          <!-- 步骤4: 确认信息 -->
          <div v-if="currentStep === 3" class="form-container">
            <div class="confirm-section">
              <h3>确认任务信息</h3>
              <div class="confirm-info">
                <div class="info-row">
                  <span class="label">Twitch账号</span>
                  <span class="value">{{ formData.account }}</span>
                </div>
                <div class="info-row">
                  <span class="label">选择游戏</span>
                  <span class="value">{{ selectedGameName }}</span>
                </div>
                <div class="info-row">
                  <span class="label">授权状态</span>
                  <span class="value success">已授权</span>
                </div>
              </div>
            </div>
  
            <div class="form-actions">
              <button 
                @click="createTask" 
                class="submit-btn"
                :disabled="isCreating"
              >
                {{ isCreating ? '创建中...' : '确认创建' }}
              </button>
              <button @click="previousStep" class="cancel-btn">
                上一步
              </button>
            </div>
          </div>
        </div>
  
        <!-- 右侧游戏奖励信息区域 -->
        <div class="rewards-section" v-if="showGameRewards">
          <h3 class="rewards-title">当前可获得奖励</h3>
          <div class="rewards-container">
            <div 
              v-for="reward in selectedGameRewards" 
              :key="reward.id"
              class="reward-card"
            >
              <div class="reward-image">
                <img :src="reward.imagePath" :alt="reward.title">
              </div>
              <div class="reward-info">
                <h4 class="reward-title">{{ reward.title }}</h4>
                <p class="reward-desc">{{ reward.description }}</p>
                <a 
                  v-if="reward.link" 
                  :href="reward.link" 
                  target="_blank" 
                  class="reward-link"
                >
                  查看详情
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 右侧未选择游戏时的提示 -->
        <div class="rewards-section empty" v-else>
          <div class="empty-state">
            <span class="empty-icon">🎮</span>
            <p>请选择一个游戏查看可获得的奖励</p>
          </div>
        </div>
      </div>
    </div>
  </template>
  
<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import ToastNotification from "@/components/common/ToastNotification.vue";

const router = useRouter()
const currentStep = ref(0)
const showPassword = ref(false)
const isSubmitting = ref(false)
const isVerifying = ref(false)
const isCreating = ref(false)
const isCopied = ref(false)
const authorizationCode = ref('')
const authToken = ref('')
const userId = ref('')

const showToast = ref(false)
const toastConfig = ref({
        type: "info", // 默认类型为 info
        title: "", // 默认无标题
        message: "", // 默认无消息
        duration: 3000, // 默认显示 3 秒
        autoClose: true, // 默认自动关闭
    })

const authUrl = ref('https://www.twitch.tv/activate')
// 游戏列表数据
const games = ref([
  //{ id: 1, name: 'PUBG' },
  { id: 547208385, name: 'Vaultbreakers' },
  { id: 515025, name: 'Overwatch 2' },
  { id: 66366, name: 'War Thunder' },
  { id: 460630, name: "Tom Clancy's Rainbow Six Siege" },
  { id: 518186, name: "Party Animals" },
  { id: 263490, name: "Rust" },
  { id: 66170, name: 'Warframe' },
  { id: 511224, name: 'Apex Legends' }
])
// 步骤定义
const steps = [
  { key: 'account', title: '账号信息' },
  { key: 'game', title: '选择游戏' },
  { key: 'auth', title: '设备授权' },
  { key: 'confirm', title: '确认创建' }
]
// 选中游戏的奖励信息
const selectedGameRewards = ref([])
// 表单数据
const formData = ref({
  account: '',
  password: '',
  cdkey: '',
  gameId: '',
  subs: [] // 存储已添加的主播
})

// 主播输入框的值
const streamerInput = ref('')

// 添加主播
const addStreamer = () => {
  const streamer = streamerInput.value.trim()
  if (streamer && formData.value.subs.length < 3) {
    if (!formData.value.subs.includes(streamer)) {
      formData.value.subs.push(streamer)
      streamerInput.value = '' // 清空输入框
    } else {
      alert('该主播已添加')
    }
  }
}

// 移除主播
const removeStreamer = (streamer) => {
  const index = formData.value.subs.indexOf(streamer)
  if (index > -1) {
    formData.value.subs.splice(index, 1)
  }
}

// 修改表单验证，主播变为可选项
const isStep2Valid = computed(() => {
  return formData.value.gameId // 只需要选择游戏即可
})

// 表单验证
const isStep1Valid = computed(() => {
  return formData.value.account && 
         formData.value.password && 
         formData.value.cdkey
})

//const isStep2Valid = computed(() => {
//  return formData.value.gameId
//})

// 是否显示游戏奖励
const showGameRewards = computed(() => {
  return currentStep.value === 1 && selectedGameRewards.value.length > 0
})

// 选中游戏名称
const selectedGameName = computed(() => {
  const game = games.value.find(g => g.id === formData.value.gameId)
  return game ? game.name : ''
})

// 切换密码显示
const togglePassword = () => {
  showPassword.value = !showPassword.value
}

// 处理游戏选择变化
const handleGameChange = async () => {
  const gameId = formData.value.gameId
  if (!gameId) {
    selectedGameRewards.value = []
    return
  }
  
  try {
    // TODO: 替换为实际的API调用
    const response = await fetch(`/api/games/${gameId}/rewards`)
    const data = await response.json()
    
    // 预期的返回数据结构示例：
    // {
    //   rewards: [
    //     {
    //       id: 1,
    //       title: "限定皮肤",
    //       description: "游戏专属皮肤",
    //       imagePath: "/rewards/skin1.jpg",
    //       link: "https://..."
    //     },
    //     // ... 更多奖励
    //   ]
    // }
    
    selectedGameRewards.value = data.rewards
  } catch (error) {
    console.error('获取游戏奖励信息失败:', error)
    selectedGameRewards.value = []
  }
}

// 步骤控制
const nextStep = () => {
    
  if (currentStep.value < steps.length - 1) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// 提交初始数据
const submitInitialData = async () => {
  if (isSubmitting.value) return
  
  try {
    isSubmitting.value = true
    // TODO: 实现数据提交和校验逻辑
    const response = await fetch('/api/tasks/validate', {
      method: 'POST',
      body: JSON.stringify(formData.value)
    })
    const data = await response.json()
    
    if (data.success) {
      authorizationCode.value = data.authorizationCode
      authToken.value = data.token
      userId.value = data.user_id
      nextStep()
    } else {
      alert(data.message)
    }
  } catch (error) {
    console.error('提交数据失败:', error)
    alert('提交失败，请重试')
  } finally {
    isSubmitting.value = false
  }
}


// 复制授权码
const copyAuthCode = async () => {
  try {
    await navigator.clipboard.writeText(authorizationCode.value)
    isCopied.value = true
    setTimeout(() => {
      isCopied.value = false
    }, 2000)
  } catch (error) {
    console.error('复制失败:', error)
  }
}

// 验证授权状态
const verifyAuthorization = async () => {
  if (isVerifying.value) return
  
  try {
    isVerifying.value = true

    const params = new URLSearchParams({
        user_id: userId.value,
        token: authToken.value,
        ...formData.value
    });

    const response = await fetch(`/api/tasks/verify-auth?${params.toString()}`, {
        method: 'GET'
    });
    const data = await response.json()
    
    if (data.isAuthorized) {
      currentStep.value = 2
      nextStep()
    } else {
      alert('未检测到授权，请完成授权后重试')
    }
  } catch (error) {
    console.error('验证授权失败:', error)
    alert('验证失败，请重试')
  } finally {
    isVerifying.value = false
  }
}


// 返回任务列表
const goToTaskList = () => {
  //router.push('/dashboard/autotask/list')
}
// 创建任务前的最终确认
const createTask = async () => {
  if (isCreating.value) return
  
  try {
    isCreating.value = true
    // TODO: 实现最终任务创建逻辑
    const response = await fetch('/api/tasks/create', {
      method: 'PUT',
      body: JSON.stringify({
        ...formData.value,
        token: authToken.value,
        user_id: userId.value,
        game_name: ""

      })
    })
    const data = await response.json()
    
    if (data.success) {
      router.push('/dashboard/autotask/list')
    } else {
      alert(data.message)
    }
  } catch (error) {
    console.error('创建任务失败:', error)
    alert('创建失败，请重试')
  } finally {
    isCreating.value = false
  }
}

</script>

<style scoped>
.task-create {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.create-container {
  display: flex;
  gap: 24px;
  min-height: calc(100vh - 140px);
}

.form-section {
  flex: 1;
  max-width: 480px;
}

.section-title {
  font-size: 24px;
  font-weight: 500;
  color: white;
  margin: 0;
}

.section-desc {
  color: #a8a4b4;
  margin: 8px 0 24px;
}

.form-container {
  background-color: #211d2d;
  border-radius: 8px;
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #a8a4b4;
}

.form-input,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #3d3654;
  background-color: #2d2640;
  color: white;
  font-size: 14px;
  outline: none;
}

.password-input {
  position: relative;
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a8a4b4;
  cursor: pointer;
  padding: 4px;
}

.form-actions {
  margin-top: 32px;
  display: flex;
  gap: 12px;
}

.submit-btn,
.cancel-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn {
  background-color: #7c3aed;
  color: white;
}

.submit-btn:hover:not(:disabled) {
  background-color: #6d28d9;
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.cancel-btn {
  background-color: #2d2640;
  color: white;
  border: 1px solid #3d3654;
}

.cancel-btn:hover {
  background-color: #3d3654;
}

.rewards-section {
  flex: 1;
  background-color: #211d2d;
  border-radius: 8px;
  padding: 24px;
}

.rewards-title {
  font-size: 18px;
  font-weight: 500;
  color: white;
  margin: 0 0 20px 0;
}

.rewards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
  padding-right: 8px;
}

.reward-card {
  background-color: #2d2640;
  border-radius: 6px;
  overflow: hidden;
}

.reward-image {
  width: 100%;
  height: 140px;
  background-color: #1a1625;
}

.reward-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reward-info {
  padding: 16px;
}

.reward-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
  margin: 0 0 8px 0;
}

.reward-desc {
  font-size: 14px;
  color: #a8a4b4;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.reward-link {
  display: inline-block;
  padding: 6px 12px;
  background-color: #3d3654;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 12px;
  transition: background-color 0.2s;
}

.reward-link:hover {
  background-color: #4a4368;
}

.empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a8a4b4;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

/* 自定义滚动条样式 */
.rewards-container::-webkit-scrollbar {
  width: 6px;
}

.rewards-container::-webkit-scrollbar-track {
  background: #2d2640;
  border-radius: 3px;
}

.rewards-container::-webkit-scrollbar-thumb {
  background: #3d3654;
  border-radius: 3px;
}

.rewards-container::-webkit-scrollbar-thumb:hover {
  background: #4a4368;
}

@media (max-width: 1024px) {
  .create-container {
    flex-direction: column;
  }

  .form-section {
    max-width: none;
  }

  .rewards-container {
    max-height: 600px;
  }
}

@media (max-width: 768px) {
  .task-create {
    padding: 16px;
  }

  .form-container,
  .rewards-section {
    padding: 16px;
  }

  .rewards-container {
    grid-template-columns: 1fr;
  }
}

/* 新增步骤指示器样式 */
.steps-indicator {
  display: flex;
  justify-content: space-between;
  margin: 24px 0;
  position: relative;
}

.steps-indicator::before {
  content: '';
  position: absolute;
  top: 16px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #3d3654;
  z-index: 0;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #2d2640;
  border: 2px solid #3d3654;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a8a4b4;
  font-weight: 500;
}

.step-text {
  font-size: 14px;
  color: #a8a4b4;
}

.step-item.active .step-number {
  background-color: #7c3aed;
  border-color: #7c3aed;
  color: white;
}

.step-item.active .step-text {
  color: white;
}

.step-item.complete .step-number {
  background-color: #059669;
  border-color: #059669;
  color: white;
}

.step-item.complete .step-text {
  color: #059669;
}

/* 授权码部分样式 */
.auth-section {
  background-color: #2d2640;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 24px;
}

.auth-code {
  text-align: center;
  margin-bottom: 24px;
}

.auth-code h3 {
  color: white;
  font-size: 16px;
  margin: 0 0 16px 0;
}

.code-display {
  background-color: #1a1625;
  padding: 16px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 24px;
  color: #7c3aed;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.copy-btn {
  padding: 6px 12px;
  background-color: #3d3654;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.copy-btn:hover {
  background-color: #4a4368;
}

.copy-btn.copied {
  background-color: #059669;
}

.auth-instructions {
  color: #a8a4b4;
}

.auth-instructions h4 {
  color: white;
  margin: 0 0 12px 0;
  font-size: 14px;
}

.auth-instructions ol {
  margin: 0;
  padding-left: 20px;
}

.auth-instructions li {
  margin-bottom: 8px;
  font-size: 14px;
}

.auth-instructions a {
  color: #7c3aed;
  text-decoration: none;
}

.auth-instructions a:hover {
  text-decoration: underline;
}

/* 确认信息部分样式 */
.confirm-section {
  margin-bottom: 24px;
}

.confirm-section h3 {
  color: white;
  font-size: 16px;
  margin: 0 0 16px 0;
}

.confirm-info {
  background-color: #2d2640;
  border-radius: 6px;
  padding: 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #3d3654;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  color: #a8a4b4;
}

.info-row .value {
  color: white;
}

.info-row .value.success {
  color: #059669;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .steps-indicator {
    margin: 16px 0 24px;
  }
  
  .step-text {
    font-size: 12px;
  }
  
  .code-display {
    font-size: 18px;
    flex-direction: column;
    gap: 12px;
  }
  
  .copy-btn {
    width: 100%;
  }
  
  .auth-section {
    padding: 16px;
  }
  
  .confirm-info {
    padding: 12px;
  }
  
  .info-row {
    flex-direction: column;
    gap: 4px;
  }
  
  .info-row .value {
    padding-left: 12px;
  }
}

.streamer-input-wrapper {
  position: relative;
}

.streamer-count {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a8a4b4;
  font-size: 12px;
}

.streamers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.streamer-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #2d2640;
  border: 1px solid #3d3654;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.streamer-tag:hover {
  background-color: #3d3654;
}

.streamer-name {
  color: white;
  font-size: 14px;
}

.remove-icon {
  color: #a8a4b4;
  font-size: 16px;
  font-weight: bold;
}

.input-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #a8a4b4;
}

.form-input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>